import _ from 'lodash';
import CalendarUtils from './CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import moment, { Moment, unitOfTime } from 'moment-timezone';
import StaffUtils, { PERMISSIONS } from 'services/StaffUtils';
import PatientUtils from 'services/PatientUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import {
  Questionnaire,
  Shift,
  PhysicianPhysicianTypeEnum,
  Appointment,
  Patient,
  Physician,
  Admin,
  AppointmentConcern,
  ConcernRequirement,
  BookingFlowOption,
} from 'generated/api';
import AIUtils from 'services/AIUtils';
import config from 'config';

const DEFAULT_ARRAY: any[] = [];

export const APPOINTMENT_STATUS = {
  booked: 'Booked',
  scheduled: 'Scheduled',
  checked_in: 'Checked-in',
  no_show: 'No-show',
  started: 'Started',
  cancelled: 'Cancelled',
  completed: 'Completed',
} as const;

const APPOINTMENT_TYPES = {
  video: 'Video',
  home: 'Home',
  clinic: 'Clinic',
  blocked: 'Blocked',
  lab: 'Lab',
} as const;

const TELEMEDICINE_METHODS = {
  phone: 'Phone',
  video: 'Video',
} as const;

interface StatusHistoryItem {
  by_role?: string;
  by_id?: string;
  status?: string;
  physician_id?: string;
  appointment_date?: number;
  appointment_length?: number;
  date?: number;
  concern?: string;
  appointment_type?: string;
}

interface AppointmentEvent {
  type: string;
  tooltipText: string;
  text: string;
  patientName: string | undefined;
  patient: Patient | undefined;
  patient_id: string;
  appointment: Appointment;
  edited: any;
  draggable: boolean;
  editable: boolean;
  start: Date | undefined;
  end: Date | undefined;
  appointment_id: string;
}

class AppointmentUtils {
  calculateDuration(
    appointment: Appointment,
    unitOfTime: unitOfTime.Base = 'minutes',
  ): number | undefined {
    const start = moment.unix(
      (appointment as any).date_called_in ||
        (appointment as any).date_checked_in ||
        appointment.date!,
    );
    const end = moment.unix((appointment as any).date_finished || (appointment as any).date_end);

    if (end) {
      return end.diff(start, unitOfTime);
    }
  }

  appointmentsToEvents(
    appointments: Appointment[],
    patients: Record<string, Patient>,
    physicians: Record<string, Physician> = {},
    showActualTime = false,
  ): AppointmentEvent[] {
    if (!appointments) return [];

    const TYPES = this.getAppointmentTypes();

    return appointments
      .map((appointment) => {
        const patient = patients[appointment.patient_id!];

        let date = appointment.date;

        const { primary_physician } = patient || {};
        const primaryPhysician = primary_physician ? physicians[primary_physician] : undefined;

        if (showActualTime) date = (appointment as any).date_called_in || appointment.date;

        let startDate: Date | undefined,
          endDate: Date | undefined,
          text = '';
        let patientName = PatientUtils.getName(patient, undefined, false, false);

        if (date) {
          startDate = moment.unix(date).toDate();
          text = CalendarUtils.format(date, 'h:mm A') + ' ' + text;

          let duration = this.getDuration(appointment);
          endDate = moment(startDate).clone().add(duration, 'hours').toDate();

          if ((appointment as any).date_end) {
            let date2 = (appointment as any).date_end;

            if (showActualTime)
              date2 = (appointment as any).date_finished || (appointment as any).date_end;

            endDate = moment.unix(date2).toDate();
            if ((appointment as any).type === TYPES.blocked) {
              text = `${CalendarUtils.format(date, 'h:mm A')}-${CalendarUtils.format(
                (appointment as any).date_end,
                'h:mm A',
              )}`;
            }
          }

          let start = moment(startDate);
          let end = moment(endDate);

          if ((appointment as any).blocked_minutes_before)
            start.add(-(appointment as any).blocked_minutes_before, 'minute');
          if ((appointment as any).blocked_minutes_after)
            end.add((appointment as any).blocked_minutes_after, 'minute');
          if ((appointment as any).display_duration)
            end = moment(start)
              .clone()
              .add((appointment as any).display_duration, 'minute');

          startDate = start.toDate();
          endDate = end.toDate();
        }

        if ((appointment as any).type === TYPES.blocked) {
          let reason = (appointment as any).complaints || 'Time Off';
          text += `: ${reason}`;
        } else {
          text += patientName;
        }

        let title = this.getConcernComplaintsText(appointment) || text;

        if (patient)
          title = `Patient: ${PatientUtils.getName(patient)} ${PatientUtils.getAge(
            patient,
          )} ${PatientUtils.getGender(patient, true)}\n\n${title}`;

        if (primaryPhysician && (patient as any).is_subscribed !== false)
          title = `Primary NP: ${StaffUtils.getName(primaryPhysician)}\n${title}`;

        if ((appointment as any).translation_language) {
          title += `\n\nTranslator Required: ${(appointment as any).translation_language}`;
        }

        return {
          type: 'appointment',
          tooltipText: title,
          text,
          patientName,
          patient,
          patient_id: appointment.patient_id!,
          appointment: {
            ...appointment,
            edited: undefined,
          } as Appointment,
          edited: (appointment as any).edited,
          draggable:
            (appointment as any).draggable !== undefined ? (appointment as any).draggable : true,
          editable:
            (appointment as any).editable !== undefined ? (appointment as any).editable : true,
          start: startDate,
          end: endDate,
          appointment_id: appointment.id!,
        };
      })
      .filter((event) => event.start !== undefined);
  }

  getAppointmentStatus() {
    return APPOINTMENT_STATUS;
  }

  getAppointmentTypes() {
    return APPOINTMENT_TYPES;
  }

  getTelemedicineMethod() {
    return TELEMEDICINE_METHODS;
  }

  getStatusHistory(
    appointment: Appointment,
    physicians: Record<string, Physician> = {},
    admins: Record<string, Admin> = {},
    patient?: Patient,
  ): string {
    let lastStatus: string | undefined,
      lastType: string | undefined,
      lastConcern: string | undefined,
      lastPhysicianId: string | undefined,
      lastAppointmentDate: number | undefined,
      lastAppointmentLength: number | undefined;

    return ((appointment as any).status_history || [])
      .filter((item: StatusHistoryItem) => !!item.date)
      .map((item: StatusHistoryItem) => {
        let {
          by_role = '',
          by_id,
          status,
          physician_id,
          appointment_date,
          appointment_length,
          date,
          concern,
          appointment_type,
        } = item;

        let byWhom = '';

        if (by_role.toLowerCase() === 'system') {
          byWhom = 'Care& System';
        } else if (by_role !== 'patient') {
          byWhom = 'Staff';
          let staff = (by_id && physicians[by_id]) || (by_id && admins[by_id]);
          if (staff) {
            byWhom = `${StaffUtils.getName(staff)} (Staff)`;
          }
        } else {
          byWhom = 'Patient';
          if (patient) {
            byWhom = `${PatientUtils.getName(patient)} (Patient)`;
          }
        }

        const dateChanged = CalendarUtils.format(date!, 'DD/MM/YY h:mm A');
        let result: string;

        if (!physician_id && !appointment_date) {
          result = `${byWhom} at ${dateChanged}\n${status}`;
        } else {
          const appointmentDate = CalendarUtils.format(appointment_date!, 'DD/MM/YY h:mm A');
          const physician = physician_id ? physicians[physician_id] : undefined;
          result = `${byWhom} at ${dateChanged}\n`;
          if (status !== lastStatus) result += `Status: ${status} `;
          if (concern !== lastConcern) result += `Concern: ${concern} `;
          if (appointment_type !== lastType) result += `Type: ${appointment_type} `;
          if (appointment_date !== lastAppointmentDate) result += `Date: ${appointmentDate} `;
          if (physician && physician_id !== lastPhysicianId)
            result += `NP: ${PhysicianUtils.getName(physician)} `;
          if (appointment_length && appointment_length !== lastAppointmentLength) {
            result += `Duration: ${appointment_length / 60}m`;
          }
          result += '\n';
        }

        lastType = appointment_type;
        lastConcern = concern;
        lastStatus = status;
        lastAppointmentDate = appointment_date;
        lastPhysicianId = physician_id;
        lastAppointmentLength = appointment_length;
        return result;
      })
      .join('\n');
  }

  getAvailablePhysicians(
    physicians: Record<string, Physician> = {},
    shifts: Shift[] = [],
    clinicId: string,
    patientId: string,
    concern?: AppointmentConcern,
    currentPhysicianId?: string,
  ): Physician[] {
    const result: Physician[] = [];
    const shiftUserIds = new Set(shifts.map(({ user_id }) => user_id));

    for (const id in physicians) {
      const physician = physicians[id]!;

      if (!StaffUtils.isActiveUser(physician)) continue;

      const {
        clinic_id,
        intern_of,
        blacklisted_patients = [],
        physician_type = PhysicianPhysicianTypeEnum.NursePractitioner,
      } = physician;

      // Check if physician is the current physician and should be added regardless of other filters
      if (id === currentPhysicianId) {
        result.push(physician);
        continue;
      }

      // Skip if physician is an intern
      if (intern_of) continue;

      // Check if physician's clinic matches or is in the shifts list
      if (clinic_id !== clinicId && !shiftUserIds.has(id)) continue;

      // Check if patient is not blacklisted
      if (blacklisted_patients.includes(patientId)) continue;

      // Check if physician's type matches the concern's required types
      if (concern?.physician_types && !concern.physician_types.includes(physician_type)) continue;

      result.push(physician);
    }

    return result;
  }

  getPatientIcon(appointment: Appointment = {} as Appointment): string {
    if (((appointment as any).concern_text || '').toLowerCase().includes('covid')) return 'virus';
    else if (
      (appointment as any).type === this.getAppointmentTypes().video &&
      (appointment as any).telemedicine_method === this.getTelemedicineMethod().phone
    )
      return 'phone';
    else if ((appointment as any).type === this.getAppointmentTypes().video) return 'video';
    else if ((appointment as any).type === this.getAppointmentTypes().home) return 'house';
    else if ((appointment as any).type === this.getAppointmentTypes().clinic) return 'location-dot';
    else if ((appointment as any).type === this.getAppointmentTypes().lab) return 'syringe';
    return 'calendar-clock';
  }

  getIcon(appointment: Appointment = {} as Appointment): string {
    if (((appointment as any).concern_text || '').toLowerCase().includes('covid')) return 'virus';
    else if (
      (appointment as any).type === this.getAppointmentTypes().video &&
      (appointment as any).telemedicine_method === this.getTelemedicineMethod().phone
    )
      return 'phone';
    else if ((appointment as any).type === this.getAppointmentTypes().video) return 'video';
    else if ((appointment as any).type === this.getAppointmentTypes().home) return 'truck-medical';
    else if ((appointment as any).type === this.getAppointmentTypes().clinic) return 'hospital';
    else if ((appointment as any).type === this.getAppointmentTypes().lab) return 'syringe';
    return 'calendar-clock';
  }

  getDuration(appointment: Appointment): number {
    // in hours

    if ((appointment as any).capacity_duration_minutes) {
      let minutes = (appointment as any).capacity_duration_minutes;
      return minutes / 60.0;
    } else if ((appointment as any).date_end) {
      let minutes = moment
        .unix((appointment as any).date_end)
        .diff(moment.unix(appointment.date!), 'minute');
      return minutes / 60.0;
    }

    const { video, home, clinic, blocked } = this.getAppointmentTypes();

    if ((appointment as any).type === video) return 30.0 / 60.0;
    else if ((appointment as any).type === clinic) return 15.0 / 60.0;
    else if ((appointment as any).type === blocked) return 30.0 / 60.0;
    else if ((appointment as any).type === home) return 30.0 / 60.0;

    return 15.0 / 60.0;
  }

  calculateEndDate(appointment: Appointment, durationInHours: number): number {
    return moment.unix(appointment.date!).add(durationInHours, 'hour').unix();
  }

  getConcernText(appointment: Appointment): string {
    let result = '';

    if ((appointment as any).concern_text) result += (appointment as any).concern_text;
    if ((appointment as any).concern_minutes)
      result += ` (${(appointment as any).concern_minutes} min)`;

    return result;
  }

  isActive(appointment: Appointment): boolean {
    let { cancelled, completed, no_show } = this.getAppointmentStatus();
    return (
      appointment.status !== cancelled &&
      appointment.status !== completed &&
      appointment.status !== no_show
    );
  }

  isBlockedSlot(appointment: Appointment): boolean {
    return (appointment as any).type === this.getAppointmentTypes().blocked;
  }

  filterByStatus(appointments: Appointment[] = [], status: string): Appointment[] {
    return appointments.filter((appointment) => appointment.status !== status);
  }

  getAppointmentDescription(
    appointment: Appointment = {} as Appointment,
    includeStatus = false,
  ): string | undefined {
    let description: string | undefined = undefined;

    if (this.isPhoneAppointment(appointment)) description = 'Phone';
    else {
      switch ((appointment as any).type) {
        case this.getAppointmentTypes().clinic:
          description = 'Clinic';
          break;
        case this.getAppointmentTypes().video:
          description = 'Video';
          break;
        case this.getAppointmentTypes().blocked:
          description = 'Time Off';
          break;
        default:
          description = (appointment as any).type;
      }
    }

    if (includeStatus && !!appointment.status) description += ` (${appointment.status})`;

    return description;
  }

  isPhoneAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (
      (appointment as any).type === this.getAppointmentTypes().video &&
      (appointment as any).telemedicine_method === this.getTelemedicineMethod().phone
    );
  }

  isVideoAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (
      (appointment as any).type === this.getAppointmentTypes().video &&
      (appointment as any).telemedicine_method === this.getTelemedicineMethod().video
    );
  }

  getMissingPatients(
    appointments: { patient_id?: string }[],
    patients: Record<string, Patient>,
  ): string[] {
    let patientsToGet: Record<string, boolean> = {};
    let patientsToGetIds: string[] = [];

    appointments.forEach((appointment) => {
      if (appointment.patient_id && !patients[appointment.patient_id]) {
        if (!patientsToGet[appointment.patient_id]) {
          patientsToGet[appointment.patient_id] = true;
          patientsToGetIds.push(appointment.patient_id);
        }
      }
    });

    return patientsToGetIds.length === 0 ? DEFAULT_ARRAY : patientsToGetIds.slice(0, 50);
  }

  getMostRecent(appointments: Appointment[] = []): Appointment | undefined {
    if (appointments.length === 0) return undefined;

    let mostRecent = appointments[0]!;

    appointments.forEach((appointment) => {
      if ((mostRecent as any).date_booking < (appointment as any).date_booking)
        mostRecent = appointment;
    });

    return mostRecent;
  }

  getUpcomingAppointments(appointments: Appointment[] = []): Appointment[] {
    let filtered = appointments.filter(
      (appointment) =>
        this.isActive(appointment) && appointment.date! > moment().add(-1, 'hour').unix(),
    );
    return PerformanceUtils.sortBy(filtered, ['date'], true);
  }

  getUpcomingAppointment(appointments: Appointment[] = []): Appointment | undefined {
    let upcoming = this.getUpcomingAppointments(appointments);
    let now = moment().unix();
    let upcomingFuture = upcoming.filter(
      (appointment) => ((appointment as any).date_end || appointment.date!) >= now,
    );

    upcomingFuture = PerformanceUtils.sortBy(upcomingFuture, ['date'], false);

    return upcomingFuture[0];
  }

  getPastAppointments(appointments: Appointment[] = []): Appointment[] {
    let filtered = appointments.filter((appointment) => !this.isActive(appointment));
    return PerformanceUtils.sortBy(filtered);
  }

  isClinicAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (
      (appointment as any).type === this.getAppointmentTypes().clinic ||
      (appointment as any).type === this.getAppointmentTypes().lab
    );
  }

  isLabAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (appointment as any).type === this.getAppointmentTypes().lab;
  }

  isHomeAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (appointment as any).type === this.getAppointmentTypes().home;
  }

  isTelemedicineAppointment(appointment: Appointment = {} as Appointment): boolean {
    return (appointment as any).type === this.getAppointmentTypes().video;
  }

  findAvailableSlot(slots: any[], appointment: Appointment): any | undefined {
    let chosenSlot: any = undefined;
    (appointment as any).date = moment
      .unix((appointment as any).date)
      .second(0)
      .unix();
    slots.forEach((slot) => {
      let { start, physicians } = slot;
      if (!chosenSlot && (appointment as any).date === start && physicians.length > 0) {
        chosenSlot = slot;
      }
    });
    return chosenSlot;
  }

  responseNeedsConfirmation(status: number): boolean {
    return status === 402 || status === 409;
  }

  getConcernComplaintsText(
    appointment: Appointment = {} as Appointment,
    includeQuotes = false,
  ): string {
    let text: string[] = [];

    if ((appointment as any).concern_text) text.push(this.getConcernText(appointment));

    if ((appointment as any).complaints) {
      let complaints = (appointment as any).complaints;
      if (includeQuotes) complaints = `"${(appointment as any).complaints}"`;
      let prepend =
        (appointment as any).type === this.getAppointmentTypes().blocked ? 'Reason' : 'Complaints';
      text = [...text, '', `${prepend}:`, complaints];
    }

    if ((appointment as any).notes) {
      let notes = (appointment as any).notes;
      if (includeQuotes) notes = `"${(appointment as any).notes}"`;
      text = [...text, '', 'Notes:', notes];
    }

    return text.join('\n');
  }

  isQuestionnaireOk(questionnaire?: Questionnaire): boolean {
    if (!questionnaire) return true;

    return (
      questionnaire.questions
        ?.filter(({ question }) => !question?.includes('student'))
        .find(({ answer }) => (answer || '').toLowerCase() === 'yes') === undefined
    );
  }

  getScheduleAppointments(
    allAppointments: any,
    physicians: Physician[],
    date: Moment,
    showCancelled: boolean,
    clinicId?: string,
  ): Appointment[] {
    let appointments: Appointment[] = { ...(allAppointments || {}) };

    if (physicians.length > 0) {
      appointments = filterPhysiciansAppointmentEvents(appointments, physicians);
    } else {
      appointments = getAllAppointments(appointments);
    }

    appointments = appointments.filter((appointment) => {
      return CalendarUtils.isSameDay(moment.unix(appointment.date!), date);
    });

    if (!showCancelled) {
      appointments = this.filterByStatus(appointments, this.getAppointmentStatus().cancelled);
      appointments = this.filterByStatus(appointments, this.getAppointmentStatus().no_show);
    }

    if (clinicId) {
      appointments = appointments.filter(
        (appointment) => (appointment as any).clinic_id === clinicId,
      );
    }
    return appointments;
  }

  isTranscriptionAvailable(
    appointment: Appointment = {} as Appointment,
    ownUser: any = {},
    permissions: string[] = [],
  ): boolean {
    const isSuperAdmin = permissions.includes(PERMISSIONS.super_admin);
    const appointmentHasTranscription = (appointment as any).has_transcription;
    const hasAccessToAppointment = this.hasAccessToAppointment(appointment, ownUser);

    const isDebugPatient = appointment?.patient_id === AIUtils.getDummyPatientId();
    const isDevEnv = (config as any).ENVIRONMENT === 'development';

    return (
      isDebugPatient ||
      (appointmentHasTranscription &&
        (isDevEnv || isSuperAdmin || (hasAccessToAppointment && this.isActive(appointment))))
    );
  }

  hasAccessToAppointment(appointment: Appointment, user: any): boolean {
    const ownId = user.id;
    const isIntern = !!user.intern_of;
    return (
      ownId === appointment.physician_id || user.intern_of === appointment.physician_id || isIntern
    );
  }

  hasAccessToConcern(
    patient: Patient,
    concern: AppointmentConcern,
    requirements: Record<string, ConcernRequirement>,
  ): boolean {
    if (concern.hidden_from_patient) return false;
    if (concern.disabled) return false;
    if (concern.gender && patient.gender !== concern.gender) return false;

    if (concern.requirements && concern.requirements.length > 0) {
      const reqs = _.compact(concern.requirements.map((it) => requirements[it]));
      const hasPatientMessage = reqs.some((req) => !!req.patient_message);
      const cleared = this.hasClearances(patient, concern.requirements);
      if (!cleared && !hasPatientMessage) {
        return false;
      }
    }

    if (concern.above_age_months || concern.below_age_months) {
      let age = CalendarUtils.calculateAgeNumber(patient.date_of_birth!, moment().unix(), 'months');
      if (concern.above_age_months && age! < concern.above_age_months) return false;
      if (concern.below_age_months && age! >= concern.below_age_months) return false;
    }

    return true;
  }

  hasClearances(patient: Patient, requirements: string[] | undefined): boolean {
    for (let req of requirements ?? []) {
      const clearance = patient.clearances?.find((it) => it.clearance_id === req);
      if (!clearance) {
        return false;
      }
      if (clearance.remaining_uses !== undefined && clearance.remaining_uses <= 0) {
        return false;
      }
    }
    return true;
  }

  filterBookingOptionsWithChildren(options: BookingFlowOption[]) {
    return options.filter((it) => it.concerns && it.concerns.length > 0);
  }
}

function getAllAppointments(appointments: any): Appointment[] {
  let result: any = {};

  for (let key in appointments) {
    if (appointments[key]) {
      if (appointments[key].filter !== undefined) {
        result = {
          ...result,
          ...PerformanceUtils.arrayToObject(appointments[key]),
        };
      }
    }
  }
  return PerformanceUtils.objectToArray(result);
}

function filterPhysiciansAppointmentEvents(
  appointments: any,
  selectedPhysicians: Physician[],
  allowUndefined = false,
): Appointment[] {
  let result: any = {};

  for (let key in appointments) {
    if (appointments[key] !== undefined) {
      if (appointments[key].filter !== undefined) {
        let filtered = appointments[key].filter((appointment: any) => {
          if (allowUndefined && appointment.physician_id === undefined) return true;
          let hasIntern = !!appointment.intern_of;
          let physician = selectedPhysicians.find(
            (physician) => physician.id === appointment.physician_id,
          );
          let intern = selectedPhysicians.find(
            (physician) => physician.id === appointment.intern_of,
          );

          return !!physician || (hasIntern && !!intern);
        });
        result = {
          ...result,
          ...PerformanceUtils.arrayToObject(filtered),
        };
      }
    }
  }
  return PerformanceUtils.objectToArray(result);
}

export default new AppointmentUtils();
